import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Spinner from './Spinner';
import { formatFinancialNumber, getFinancialNumberColor } from '../utils/formatUtils';
import { getCardClasses, getContainerClasses } from '../utils/styleUtils';
import { FINANCIAL } from '../config/constants';

interface FinancialData {
  gross: number;
  net: number;
  opex: number;
  ebitda: number;
}

type YearData = {
  [month: string]: FinancialData;
};

const ManagementAccount: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState<{ [year: number]: YearData }>({});
  
  // Use constants from centralized config
  const years = FINANCIAL.YEARS;
  const months = FINANCIAL.MONTHS;

  // Memoize styling classes
  const containerClasses = useMemo(() => getContainerClasses('lg'), []);
  const cardClasses = useMemo(() => getCardClasses('elevated'), []);

  // Memoize expensive calculations
  const calculatedMetrics = useMemo(() => {
    const calculateSummary = (year: number, field: keyof FinancialData) => {
      const currentYear = new Date().getFullYear();
      const yearData = financialData[year];
      if (!yearData) return 0;
      
      if (year === currentYear) {
        // YTD: sum only months that have content (non-zero values)
        return Object.values(yearData).reduce((sum, monthData) => {
          return sum + (monthData[field] !== 0 ? monthData[field] : 0);
        }, 0);
      } else {
        // FY: sum all months
        return Object.values(yearData).reduce((sum, monthData) => {
          return sum + monthData[field];
        }, 0);
      }
    };

    const getSummaryLabel = (year: number) => {
      const currentYear = new Date().getFullYear();
      return year === currentYear ? `YTD ${year}` : `FY ${year}`;
    };

    return { calculateSummary, getSummaryLabel };
  }, [financialData]);

  // Load data from Firestore on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'managementAccounts', 'main');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setFinancialData(docSnap.data().financialData);
        } else {
          // Initialize with empty data structure
          const emptyData: { [year: number]: YearData } = {};
          years.forEach(year => {
            emptyData[year] = months.reduce((acc, month) => {
              acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
              return acc;
            }, {} as YearData);
          });
          setFinancialData(emptyData);
        }
      } catch (error) {
        console.error('Error loading financial data for read-only view:', error);
        // Initialize with empty data structure on error
        const emptyData: { [year: number]: YearData } = {};
        years.forEach(year => {
          emptyData[year] = months.reduce((acc, month) => {
            acc[month] = { gross: 0, net: 0, opex: 0, ebitda: 0 };
            return acc;
          }, {} as YearData);
        });
        setFinancialData(emptyData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [years, months]); // Added dependencies

  // Memoized summary cards rendering
  const renderSummaryCards = useCallback((year: number) => {
    const metrics = [
      { key: 'gross' as keyof FinancialData, label: 'Gross Revenue', icon: TrendingUp },
      { key: 'net' as keyof FinancialData, label: 'Net Revenue', icon: TrendingUp },
      { key: 'opex' as keyof FinancialData, label: 'Operating Expenses', icon: TrendingDown },
      { key: 'ebitda' as keyof FinancialData, label: 'EBITDA', icon: TrendingUp }
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map(({ key, label, icon: Icon }) => {
          const value = calculatedMetrics.calculateSummary(year, key);
          const isPositive = value >= 0;
          return (
            <div key={key} className={`${cardClasses} p-6 hover:shadow-lg transition-shadow duration-200`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{label}</p>
                  <p className={`text-2xl font-bold ${getFinancialNumberColor(value)} mt-1`}>
                    {formatFinancialNumber(value) || '0'}
                  </p>
                </div>
                <Icon className={`w-8 h-8 ${isPositive ? 'text-emerald-500' : 'text-red-500'}`} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [calculatedMetrics, cardClasses]);

  // Memoized table rendering
  const renderTable = useCallback((year: number) => {
    if (!financialData[year]) return null;

    return (
      <div className={`${cardClasses} overflow-hidden`}>
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Financial Details - {calculatedMetrics.getSummaryLabel(year)}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100 border-l border-r border-gray-200">
                  {calculatedMetrics.getSummaryLabel(year)}
                </th>
                {months.map((month) => (
                  <th key={month} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Gross Revenue */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Gross Revenue
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                  <span className={getFinancialNumberColor(calculatedMetrics.calculateSummary(year, 'gross'))}>
                    {formatFinancialNumber(calculatedMetrics.calculateSummary(year, 'gross'))}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={getFinancialNumberColor(financialData[year][month].gross)}>
                      {formatFinancialNumber(financialData[year][month].gross)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Net Revenue */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Net Revenue
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                  <span className={getFinancialNumberColor(calculatedMetrics.calculateSummary(year, 'net'))}>
                    {formatFinancialNumber(calculatedMetrics.calculateSummary(year, 'net'))}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={getFinancialNumberColor(financialData[year][month].net)}>
                      {formatFinancialNumber(financialData[year][month].net)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* OPEX */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  OPEX
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                  <span className={getFinancialNumberColor(calculatedMetrics.calculateSummary(year, 'opex'))}>
                    {formatFinancialNumber(calculatedMetrics.calculateSummary(year, 'opex'))}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={getFinancialNumberColor(financialData[year][month].opex)}>
                      {formatFinancialNumber(financialData[year][month].opex)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* EBITDA */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  EBITDA
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-center bg-gray-50 border-l border-r border-gray-200">
                  <span className={getFinancialNumberColor(calculatedMetrics.calculateSummary(year, 'ebitda'))}>
                    {formatFinancialNumber(calculatedMetrics.calculateSummary(year, 'ebitda'))}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={month} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={getFinancialNumberColor(financialData[year][month].ebitda)}>
                      {formatFinancialNumber(financialData[year][month].ebitda)}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [financialData, months, calculatedMetrics, cardClasses]);

  if (loading) {
    return <Spinner fullScreen={true} className="bg-slate-50" />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className={`${containerClasses} py-8`}>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Management Accounts</h1>
          <p className="mt-2 text-gray-600">Financial performance overview</p>
        </div>

        {/* Year Selection */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedYear === year
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        {renderSummaryCards(selectedYear)}

        {/* Detailed Table */}
        {renderTable(selectedYear)}
      </main>
    </div>
  );
};

export default ManagementAccount; 